import { getToken, setToken, removeToken } from '../utils/tokenStore';
import axios from 'axios';
import apiConfig, { API_BASE_URL, FILE_BASE_URL } from '../config/apiConfig';

export { API_BASE_URL, FILE_BASE_URL, apiConfig };

// Helper to extract non-HttpOnly CSRF token cookie
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? decodeURIComponent(match[3]) : null;
};

export const apiClient = axios.create({
  baseURL: apiConfig.baseURL,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: apiConfig.timeout,
});

const api = apiClient;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      if (token && prom.config?.headers) {
        prom.config.headers['Authorization'] = `Bearer ${token}`;
      }
      prom.resolve(api(prom.config));
    }
  });
  failedQueue = [];
};

// Request interceptor: Attach Anti-CSRF header + Bearer token + fix FormData Content-Type
api.interceptors.request.use(
  (config) => {
    // 1. Ensure Anti-CSRF token header is attached on mutation requests if cookie exists
    const xsrfToken = getCookie('XSRF-TOKEN');
    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = xsrfToken;
    }

    // 2. Attach Authorization Bearer token if present (prioritize portal session token for portal requests, staff token for internal)
    let token = null;
    if (config.url && config.url.includes('/b2b/portal/')) {
      // a staff token must never go to the portal: the server answers 403 and the client looks broken
      token = sessionStorage.getItem('b2b_portal_token');
    } else {
      token = getToken('lis_access_token') || sessionStorage.getItem('b2b_portal_token');
    }
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // 3. Attach X-Tenant-Domain header if specified in storage for loopback/development tenant switching
    const activeTenantDomain = sessionStorage.getItem('lis_tenant_domain') || localStorage.getItem('lis_tenant_domain');
    if (activeTenantDomain && !config.headers['X-Tenant-Domain']) {
      config.headers['X-Tenant-Domain'] = activeTenantDomain;
    }

    // CRITICAL: If the body is FormData, remove the global 'application/json'
    // Content-Type so axios can auto-set 'multipart/form-data' with the correct
    // boundary string.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Automatic token refresh on 401 Unauthorized
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Client portal: a missing, expired or foreign token ends the portal session; it never goes through the staff refresh
    const isPortalCall = originalRequest.url?.includes('/b2b/portal/') && !originalRequest.url?.includes('/b2b/portal/login');
    if (isPortalCall && (error.response?.status === 401 || error.response?.status === 403)) {
      sessionStorage.removeItem('b2b_portal_token');
      sessionStorage.removeItem('b2b_portal_user');
      sessionStorage.setItem('b2b_portal_notice', 'Your session has ended. Please sign in again.');
      if (!window.location.pathname.startsWith('/b2b/login')) window.location.href = '/b2b/login';
      return Promise.reject(error);
    }

    // Skip refresh attempt on login or refresh endpoint itself
    if (
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/b2b/portal/login') ||
      originalRequest.url?.includes('/auth/face/login') ||
      originalRequest.url?.includes('/auth/passkey/login') ||
      originalRequest.url?.includes('/auth/refresh-token')
    ) {
      return Promise.reject(error);
    }

    // Check if error response indicates session revocation or tenant context mismatch
    const sessionStatus = error.response?.headers?.['x-session-status'];
    const isSessionRevoked = sessionStatus === 'REVOKED' || 
                             sessionStatus === 'FORCE_LOGGED_OUT' ||
                             (typeof error.response?.data?.message === 'string' && 
                              (error.response.data.message.includes('Your session has been ended') ||
                               error.response.data.message.includes('Session REVOKED') ||
                               error.response.data.message.includes('Session FORCE_LOGGED_OUT')));

    const isTenantMismatch = error.response?.status === 403 &&
      (error.response?.data?.error === 'TENANT_HOST_MISMATCH' ||
       error.response?.data?.error === 'TENANT_MISMATCH' ||
       error.response?.data?.error === 'TENANT_SECURITY_VIOLATION');

    if (isSessionRevoked || isTenantMismatch) {
      isRefreshing = false;
      failedQueue = [];
      localStorage.removeItem('lis_user');
      removeToken('lis_access_token');
      removeToken('lis_refresh_token');
      localStorage.removeItem('lis_login_detail_id');
      const noticeMsg = isTenantMismatch
        ? (error.response?.data?.message || 'Tenant context violation: Your session does not match this host domain.')
        : 'Your session has been ended because a new login was detected.';
      sessionStorage.setItem('lis_session_revoked_msg', noticeMsg);
      window.dispatchEvent(new CustomEvent('auth:expired', { detail: { reason: isTenantMismatch ? 'tenant_mismatch' : 'session_revoked' } }));
      if (typeof window !== 'undefined' && window.location.pathname !== '/login' && !window.location.pathname.startsWith('/b2b/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: originalRequest });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const storedRefreshToken = getToken('lis_refresh_token');
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh-token`,
          { refreshToken: storedRefreshToken || undefined },
          { withCredentials: true }
        );

        if (response.data && response.data.success) {
          const { user, accessToken, refreshToken } = response.data.data || {};
          if (user) {
            localStorage.setItem('lis_user', JSON.stringify(user));
          }
          if (accessToken) {
            setToken('lis_access_token', accessToken);
            originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          }
          if (refreshToken) {
            setToken('lis_refresh_token', refreshToken);
          }

          processQueue(null, accessToken);
          return api(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        localStorage.removeItem('lis_user');
        removeToken('lis_access_token');
        removeToken('lis_refresh_token');
        localStorage.removeItem('lis_login_detail_id');
        
        const refreshRevoked = refreshErr.response?.data?.message?.includes('Your session has been ended') ||
                               refreshErr.response?.headers?.['x-session-status'] === 'REVOKED';
        if (refreshRevoked) {
          sessionStorage.setItem('lis_session_revoked_msg', 'Your session has been ended because a new login was detected.');
        }
        window.dispatchEvent(new CustomEvent('auth:expired'));
        if (typeof window !== 'undefined' && window.location.pathname !== '/login' && !window.location.pathname.startsWith('/b2b/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

